/**
 * Page-editor adapter for the headless Review moment.
 *
 * Chat owns the review chrome (`ReviewMomentFooter`). This bridge only
 * forwards enabled state, the source-message pin, and undo / redo / diff
 * callbacks into the shared slot. Staging is a same-origin iframe, so the
 * payload must stay serializable flags + `onAction` (no editor-created React
 * tree).
 *
 * Renders nothing.
 */
import { useCallback, useEffect, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useCurrentConversationId } from '@atlassian/conversation-assistant/controllers/chat-store';
import {
	insertReviewMomentFooter,
	removeReviewMomentFooter,
} from '@atlassian/conversation-assistant/controllers/message-footer-slot/insertReviewMomentFooter';
import { getFooterSlotGeneration } from '@atlassian/conversation-assistant/controllers/message-footer-slot/messageFooterSlotStore';

export const ReviewChatBridge = ({
	available,
	canRedo,
	canUndo,
	isDisplayingDiff,
	onRedo,
	onToggleDiff,
	onUndo,
	sourceMessageId,
}: {
	available: boolean;
	canRedo: boolean;
	canUndo: boolean;
	isDisplayingDiff: boolean;
	onRedo: () => void;
	onToggleDiff: () => void;
	onUndo: () => void;
	/** Assistant message that applied this review. Pins View changes to that bubble. */
	sourceMessageId?: string;
}): null => {
	const publish = usePublish('ai-mate');
	const currentConversationId = useCurrentConversationId();
	const { mounted: isChatMounted } = useUIStore();

	// Capture at mount so a later create / conversation change (which bumps
	// the window.top store generation) cannot re-insert this editor's Undo
	// into chat, and so unmount cannot remove a newer document's footer.
	const generationRef = useRef(getFooterSlotGeneration());
	// Keep the first non-empty pin so a later session start cannot move this
	// still-mounted review onto a different (or the latest) bubble.
	const sourceMessageIdRef = useRef(sourceMessageId);
	if (sourceMessageId) {
		sourceMessageIdRef.current = sourceMessageId;
	}
	// First real conversation id seen while this review is mounted. Opening
	// Rovo chat later must land on that send, not a fresh empty thread.
	const conversationIdRef = useRef<string | undefined>();
	if (
		currentConversationId &&
		currentConversationId !== EMPTY_CONVERSATION_ID &&
		!conversationIdRef.current
	) {
		conversationIdRef.current = currentConversationId;
	}

	const onUndoRef = useRef(onUndo);
	onUndoRef.current = onUndo;
	const onRedoRef = useRef(onRedo);
	onRedoRef.current = onRedo;
	const onToggleDiffRef = useRef(onToggleDiff);
	onToggleDiffRef.current = onToggleDiff;
	const footerStateRef = useRef({ available, canRedo, canUndo, isDisplayingDiff });
	footerStateRef.current = { available, canRedo, canUndo, isDisplayingDiff };

	const onRemoteAction = useCallback((actionId: string) => {
		if (actionId === 'undo') {
			onUndoRef.current();
			return;
		}
		if (actionId === 'redo') {
			onRedoRef.current();
			return;
		}
		if (actionId === 'toggle-diff') {
			onToggleDiffRef.current();
		}
	}, []);

	useEffect(() => {
		if (!available && !isDisplayingDiff) {
			removeReviewMomentFooter(generationRef.current);
			return;
		}

		insertReviewMomentFooter(
			{
				canRedo,
				canUndo,
				isDisplayingDiff,
			},
			onRemoteAction,
			generationRef.current,
			sourceMessageIdRef.current,
		);
	}, [available, canRedo, canUndo, isDisplayingDiff, onRemoteAction, sourceMessageId]);

	useEffect(() => {
		const generation = generationRef.current;
		return () => {
			removeReviewMomentFooter(generation);
		};
	}, []);

	// When chat first opens, land on the inline send. Do not publish again if the
	// user then picks another conversation — that would pin them to this thread.
	const wasChatMountedRef = useRef(false);
	useEffect(() => {
		const channelId = conversationIdRef.current;
		const justOpened = isChatMounted && !wasChatMountedRef.current;
		wasChatMountedRef.current = isChatMounted;

		if (!isChatMounted || !channelId) {
			return;
		}

		if (justOpened && currentConversationId !== channelId) {
			publish({
				type: 'chat-open',
				source: 'review-moment',
				data: { channelId },
			});
		}

		// Switching conversation invalidates the footer slot (new generation, items
		// cleared) so a stale editor cannot keep View Changes on the wrong thread.
		// Wait a tick, then re-insert against the new generation.
		const timeoutId = window.setTimeout(() => {
			const footerState = footerStateRef.current;
			if (!footerState.available && !footerState.isDisplayingDiff) {
				return;
			}
			generationRef.current = getFooterSlotGeneration();
			insertReviewMomentFooter(
				{
					canRedo: footerState.canRedo,
					canUndo: footerState.canUndo,
					isDisplayingDiff: footerState.isDisplayingDiff,
				},
				onRemoteAction,
				generationRef.current,
				sourceMessageIdRef.current,
			);
		}, 0);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [currentConversationId, isChatMounted, onRemoteAction, publish]);

	return null;
};
